import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, Volume2, VolumeX, X, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AppContext';
import { getSoundPack, setSoundVolume } from './restTimerSounds';

// ═══════════════════════════════════════════════════════════════════════
// Iter. 411.mm7 — Background-proof rest timer.
// Fixes: user misses the "end of rest" beep when they switch to another
// app (iOS Safari / Chrome throttle setInterval and suspend AudioContext).
//
// 4 defenses layered:
//   1. WALL-CLOCK — store an absolute endTimestamp and re-derive timeLeft
//      on every tick. Interval throttling in background no longer breaks
//      the countdown UI.
//   2. VISIBILITY CATCH-UP — when the tab returns from background, re-sync
//      the UI and if the timer expired while away, play the sound NOW +
//      auto-advance to the next set.
//   3. SYSTEM NOTIFICATION — schedule a native OS notification (via the
//      service worker) so the phone rings/vibrates EVEN when the app is
//      backgrounded. Only fires if the user granted permission.
//   4. VIBRATION — navigator.vibrate on expiry (Android; iOS ignores).
// ═══════════════════════════════════════════════════════════════════════

// Web Audio API beep generator — works even with screen off.
// (Kept for the FR default fallback used by the vibration side-effect.
//  The actual warning/end sounds are now delegated to the user-selected
//  sound pack from restTimerSounds.js — Iter. 411.mm8.)
function playEndFxBonus() {
  // Bonus haptic on Android — no-op on iOS but doesn't hurt.
  try { navigator.vibrate?.([200, 100, 200, 100, 300]); } catch { /* ignore */ }
}

// Schedule a native OS notification that fires when the rest is over.
// We can't natively "schedule" on iOS/Safari, so we register a NOTIFICATION
// TAG and post the notification RIGHT WHEN the timer expires client-side.
// Because the service worker keeps running even when the tab is backgrounded
// on Chrome/Android, and iOS at least respects the notification if it fires
// while the app is still holding onto its worker.
async function ensureNotificationPermission() {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

async function fireEndNotification(fr) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = fr ? 'Repos terminé — série suivante !' : 'Rest over — next set!';
  const body = fr
    ? 'Reprends l\'exercice. YUMIX est là.'
    : 'Back to the exercise. YUMIX is watching.';
  const options = {
    body,
    icon: '/icons/icon-192x192.png',
    badge: '/icons/icon-96x96.png',
    tag: 'yumix-rest-timer',
    renotify: true,
    requireInteraction: false,
    vibrate: [200, 100, 200, 100, 300],
    data: { url: window.location.pathname || '/workouts' },
  };
  try {
    if (navigator.serviceWorker?.ready) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, options);
      return;
    }
  } catch {
    /* fall through */
  }
  // Fallback: plain window.Notification (only fires when the tab is alive)
  try { new Notification(title, options); } catch { /* ignore */ }
}

const RestTimer = ({ restSeconds = 60, onComplete, onDismiss, language = 'fr', ghost = null }) => {
  const fr = language === 'fr';
  const { user } = useAuth();
  const soundPackId = user?.rest_timer_sound || 'beep';
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  // Keep the sound pack in a ref so hot-swap of `user.rest_timer_sound`
  // doesn't require the useCallbacks below to re-fire.
  const soundPackRef = useRef(getSoundPack(soundPackId));
  useEffect(() => { soundPackRef.current = getSoundPack(soundPackId); }, [soundPackId]);

  // Iter. 411.mm11 — Hydrate the shared volume from the user profile
  // (0..100 → 0..1). If unset, keep the module default of 1.0 to preserve
  // legacy loudness.
  useEffect(() => {
    if (typeof user?.rest_timer_volume === 'number') {
      setSoundVolume(user.rest_timer_volume / 100);
    }
  }, [user?.rest_timer_volume]);

  const playWarning = useCallback(() => {
    try { soundPackRef.current.warning(); } catch { /* ignore */ }
  }, []);
  const playEnd = useCallback(() => {
    try { soundPackRef.current.end(); } catch { /* ignore */ }
    playEndFxBonus();
  }, []);
  // Wall-clock reference — the SOURCE OF TRUTH. The countdown is derived
  // from (endAtRef - Date.now()) instead of decrementing a local counter,
  // so browser throttling in background can no longer desync the UI.
  const endAtRef = useRef(Date.now() + restSeconds * 1000);
  const pauseAccumRef = useRef(0);  // total ms spent paused
  const pauseStartRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(restSeconds);
  const warnedRef = useRef(false);
  const completedRef = useRef(false);
  const notifScheduledRef = useRef(false);

  const computeTimeLeft = useCallback(() => {
    // If paused, freeze on current value
    if (pauseStartRef.current !== null) {
      return Math.max(0, Math.ceil((endAtRef.current - pauseStartRef.current) / 1000));
    }
    return Math.max(0, Math.ceil((endAtRef.current - Date.now()) / 1000));
  }, []);

  const handleExpiry = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (soundEnabled) playEnd();
    // Auto-close after a short delay so the "GO!" is visible
    setTimeout(() => onComplete?.(), 800);
  }, [soundEnabled, playEnd, onComplete]);

  // Ask notification permission once — kicks in the first time the timer
  // mounts. Skipping the ask returns immediately (no perms → no notif).
  useEffect(() => {
    (async () => {
      await ensureNotificationPermission();
    })();
  }, []);

  // Schedule the OS-level notification just once, right when the timer starts.
  // The tag `yumix-rest-timer` guarantees a fresh timer replaces the previous.
  // We fire it EXACTLY at expiry using setTimeout — this timeout survives most
  // background throttling because the OS keeps the tab's worker awake enough
  // to run once. If iOS kills the timer, the visibility catch-up still fires
  // the sound + notification the moment the user returns.
  useEffect(() => {
    if (notifScheduledRef.current) return;
    notifScheduledRef.current = true;
    const remainingMs = Math.max(0, endAtRef.current - Date.now());
    const timeoutId = setTimeout(() => {
      fireEndNotification(fr).catch(() => {});
    }, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [fr]);

  // Countdown loop — ticks 4× / sec for smooth progress ring, derives UI from
  // wall-clock, and fires warning/end sound at the right moments.
  useEffect(() => {
    if (isPaused) return;
    const tick = () => {
      const next = computeTimeLeft();
      setTimeLeft(next);
      if (next === 3 && soundEnabled && !warnedRef.current) {
        warnedRef.current = true;
        playWarning();
      }
      if (next <= 0) {
        handleExpiry();
      }
    };
    tick(); // immediate sync
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isPaused, soundEnabled, computeTimeLeft, handleExpiry, playWarning]);

  // ── Defense #2: visibility catch-up ─────────────────────────────────
  // When the tab returns from background, immediately reconcile: if the
  // timer expired while away, play the sound NOW (better late than never)
  // and auto-advance.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const next = computeTimeLeft();
      setTimeLeft(next);
      if (next <= 0 && !completedRef.current) {
        handleExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    // Also handle pageshow — iOS fires this when returning via bfcache
    window.addEventListener('pageshow', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onVisible);
    };
  }, [computeTimeLeft, handleExpiry]);

  // Pause / resume — shift the endAt forward by the pause duration so the
  // wall-clock math stays honest.
  const togglePause = () => {
    if (isPaused) {
      // Resuming: fold accumulated pause into endAt
      if (pauseStartRef.current !== null) {
        const pausedMs = Date.now() - pauseStartRef.current;
        endAtRef.current += pausedMs;
        pauseAccumRef.current += pausedMs;
        pauseStartRef.current = null;
      }
      setIsPaused(false);
    } else {
      pauseStartRef.current = Date.now();
      setIsPaused(true);
    }
  };

  const resetTimer = () => {
    endAtRef.current = Date.now() + restSeconds * 1000;
    pauseAccumRef.current = 0;
    pauseStartRef.current = null;
    warnedRef.current = false;
    completedRef.current = false;
    notifScheduledRef.current = false;
    setIsPaused(false);
    setTimeLeft(restSeconds);
    // Re-schedule the notification for the new expiry
    setTimeout(() => {
      notifScheduledRef.current = true;
      const remainingMs = Math.max(0, endAtRef.current - Date.now());
      setTimeout(() => fireEndNotification(fr).catch(() => {}), remainingMs);
    }, 0);
  };

  const pct = ((restSeconds - timeLeft) / restSeconds) * 100;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const isWarning = timeLeft <= 3 && timeLeft > 0;
  const isDone = timeLeft <= 0;

  const circumference = 2 * Math.PI * 45;
  const progress = (pct / 100) * circumference;

  return (
    <div className={`fixed inset-0 z-[1200] flex items-center justify-center bg-black/80 backdrop-blur-sm`}
      data-testid="rest-timer-overlay">
      <div className={`relative w-72 p-6 rounded-2xl border-2 transition-all duration-300 ${
        isDone ? 'bg-primary/20 border-primary shadow-[0_0_60px_rgba(var(--primary-rgb),0.3)]'
        : isWarning ? 'bg-amber-500/15 border-amber-500/50 animate-pulse'
        : 'bg-card border-border'
      }`} data-testid="rest-timer-card">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Timer className={`w-4 h-4 ${isDone ? 'text-primary' : isWarning ? 'text-amber-400' : 'text-muted-foreground'}`} />
            <span className="font-heading text-xs uppercase tracking-widest text-muted-foreground">
              {fr ? 'Repos' : 'Rest'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setSoundEnabled(!soundEnabled)} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              data-testid="rest-timer-sound-toggle">
              {soundEnabled ? <Volume2 className="w-4 h-4 text-muted-foreground" /> : <VolumeX className="w-4 h-4 text-red-400" />}
            </button>
            <button onClick={onDismiss} className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              data-testid="rest-timer-dismiss">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Circular Timer */}
        <div className="flex justify-center mb-4">
          <div className="relative w-40 h-40">
            <svg className="w-40 h-40 -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="4" className="text-secondary/50" />
              <circle cx="50" cy="50" r="45" fill="none" strokeWidth="4" strokeLinecap="round"
                className={isDone ? 'stroke-primary' : isWarning ? 'stroke-amber-400' : 'stroke-primary/60'}
                strokeDasharray={circumference}
                strokeDashoffset={circumference - progress}
                style={{ transition: 'stroke-dashoffset 0.25s linear' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isDone ? (
                <span className="font-heading text-2xl font-black text-primary">GO!</span>
              ) : (
                <>
                  <span className={`font-heading text-4xl font-black tabular-nums ${isWarning ? 'text-amber-400' : 'text-foreground'}`}>
                    {minutes > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : seconds}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-1">
                    / {restSeconds}s
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* GHOST SET — last session reference to apply positive pressure between sets */}
        {ghost && (ghost.weight || ghost.reps) && (
          <div className="mb-3 p-2.5 rounded-lg bg-secondary/40 border border-border/30 text-center" data-testid="rest-ghost-badge">
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground/80 mb-0.5">
              {fr ? 'Dernière séance' : 'Last session'}
            </p>
            <p className="text-xs font-semibold text-foreground tabular-nums">
              {ghost.weight ? `${ghost.weight} lbs` : ''}
              {ghost.weight && ghost.reps ? ' x ' : ''}
              {ghost.reps ? `${ghost.reps} reps` : ''}
            </p>
            <p className="text-[9px] text-primary/80 font-medium mt-0.5">
              {fr ? 'Peux-tu faire mieux ?' : 'Can you beat it?'}
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="flex gap-2">
          {!isDone ? (
            <>
              <button
                onClick={togglePause}
                className={`flex-1 py-2.5 rounded-xl font-heading text-xs uppercase tracking-wider transition-all ${
                  isPaused ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground hover:bg-secondary/80'
                }`}
                data-testid="rest-timer-pause"
              >
                {isPaused ? (fr ? 'Reprendre' : 'Resume') : (fr ? 'Pause' : 'Pause')}
              </button>
              <button
                onClick={resetTimer}
                className="px-3 py-2.5 rounded-xl bg-secondary text-foreground hover:bg-secondary/80 transition-all"
                data-testid="rest-timer-reset"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={onDismiss}
              className="flex-1 py-3 rounded-xl bg-primary text-primary-foreground font-heading text-sm uppercase tracking-wider"
              data-testid="rest-timer-go"
            >
              {fr ? 'Serie suivante!' : 'Next set!'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestTimer;
